import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Button, SegmentedControl, Text, Spinner } from "@radix-ui/themes";
import { Cross1Icon, ArrowLeftIcon } from "@radix-ui/react-icons";
import styles from "./enrichmentForm.module.scss";
import { useCreateEnrichment } from "@/api/enrichment";
import type { EnrichmentOption, ExtractionOutputField } from "@/v2/entities/enrichment/Enrichment.model";
import EnrichmentReviewForm from "./EnrichmentReviewForm";
import type { EnrichmentType } from "./EnrichmentReviewForm";

export type { EnrichmentType };

type CreationStep = "describe" | "review";

const TYPE_DESCRIPTIONS: Record<EnrichmentType, string> = {
  classification: "Assign each interaction to a category based on its content.",
  extraction: "Pull specific pieces of information out of each interaction.",
};

interface EnrichmentCreationFormProps {
  onSubmit: (data: {
    task: string;
    name: string;
    type: EnrichmentType;
    apiResponse: any;
  }) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

function EnrichmentCreationForm({
  onSubmit,
  onCancel,
  loading = false,
}: EnrichmentCreationFormProps) {
  const [enrichmentType, setEnrichmentType] =
    useState<EnrichmentType>("classification");
  const [prompt, setPrompt] = useState("");
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [editedOptions, setEditedOptions] = useState<EnrichmentOption[]>([]);
  const [editedName, setEditedName] = useState("");
  const [editedTask, setEditedTask] = useState("");
  const [editedOutputFields, setEditedOutputFields] = useState<ExtractionOutputField[]>([]);
  const [editedTrigger, setEditedTrigger] = useState("ON_FINITE_STATE");
  const [step, setStep] = useState<CreationStep>("describe");

  const createEnrichmentMutation = useCreateEnrichment();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [prompt]);

  const extractOptions = useCallback((response: any): EnrichmentOption[] => {
    const rawOptions = response?.options || response?.output_with_options?.[0]?.options || [];
    return rawOptions.map((opt: any) => ({
      name: opt.name || "",
      value: opt.value || opt.name?.toLowerCase().replace(/\s+/g, "_") || "",
      brief: opt.brief || "",
    }));
  }, []);

  const extractOutputFields = useCallback((response: any): ExtractionOutputField[] => {
    const rawFields = response?.output || response?.output_with_options || [];
    return rawFields.map((f: any) => ({
      name: f.name || "",
      key: f.key || "",
      brief: f.brief || "",
    }));
  }, []);

  const handleTypeChange = useCallback((value: string) => {
    setEnrichmentType(value as EnrichmentType);
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return;

    try {
      const data = await createEnrichmentMutation.mutateAsync({
        text: prompt,
        type: enrichmentType,
      });
      setApiResponse(data);
      setEditedName(data.name || "");
      setEditedTask(data.task || prompt);
      setEditedTrigger(data.trigger || "ON_FINITE_STATE");
      if (enrichmentType === "classification") {
        setEditedOptions(extractOptions(data));
      } else {
        setEditedOutputFields(extractOutputFields(data));
      }
      setStep("review");
    } catch (error) {
      console.error("error calling API:", error);
      setApiResponse({ error: "Failed to call API" });
    }
  }, [prompt, enrichmentType, createEnrichmentMutation, extractOptions, extractOutputFields]);

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setPrompt(e.target.value);
    },
    []
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditedName(e.target.value);
    },
    []
  );

  const handleTaskChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setEditedTask(e.target.value);
    },
    []
  );

  const handleBack = useCallback(() => {
    if (step === "review") {
      setStep("describe");
    }
  }, [step]);

  const handleSubmit = useCallback(() => {
    if (!apiResponse || !editedName.trim()) return;

    const isClassification = enrichmentType === "classification";
    const submittedResponse = isClassification
      ? { ...apiResponse, options: editedOptions, trigger: editedTrigger }
      : { ...apiResponse, output: editedOutputFields, trigger: editedTrigger };

    onSubmit({
      task: editedTask,
      name: editedName.trim(),
      type: enrichmentType,
      apiResponse: submittedResponse,
    });
  }, [apiResponse, enrichmentType, editedName, editedOptions, editedTask, editedOutputFields, editedTrigger, onSubmit]);

  const placeholder = useMemo(
    () =>
      enrichmentType === "classification"
        ? 'Describe how you want to classify each interaction.\ne.g. "Classify each ticket by sentiment: positive, negative, or neutral"'
        : 'Describe what you want to extract from each interaction.\ne.g. "Extract the main topic discussed and a short summary"',
    [enrichmentType]
  );

  const isLoading = createEnrichmentMutation.isLoading;

  const headerTitle = useMemo(() => {
    if (step === "review") return "Review Enrichment";
    return "New Enrichment";
  }, [step]);

  return (
    <div className={styles.enrichmentFormContainer}>
      <div className={styles.enrichmentFormHeader}>
        <div className={styles.enrichmentFormHeaderLeft}>
          <button
            onClick={step === "describe" ? onCancel : handleBack}
            className={styles.enrichmentFormCloseButton}
            aria-label={step === "describe" ? "Close" : "Back"}
            type="button"
          >
            {step === "describe" ? (
              <Cross1Icon style={{ width: "16px", height: "16px", color: "var(--gray-11)" }} />
            ) : (
              <ArrowLeftIcon style={{ width: "16px", height: "16px", color: "var(--gray-11)" }} />
            )}
          </button>
          <span className={styles.enrichmentFormHeaderTitle}>
            {headerTitle}
          </span>
        </div>
        <div className={styles.enrichmentFormHeaderActions}>
        </div>
      </div>

      <div className={styles.enrichmentFormMainContent}>
        {step === "describe" && (
          <div className={styles.centeredColumn}>
            <div className={styles.typeToggleWrapper}>
              <SegmentedControl.Root
                value={enrichmentType}
                onValueChange={handleTypeChange}
                size="2"
              >
                <SegmentedControl.Item value="classification">
                  Classification
                </SegmentedControl.Item>
                <SegmentedControl.Item value="extraction">
                  Extraction
                </SegmentedControl.Item>
              </SegmentedControl.Root>
            </div>

            <Text size="2" color="gray" align="center">
              {TYPE_DESCRIPTIONS[enrichmentType]}
            </Text>

            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={handleTextareaChange}
              placeholder={placeholder}
              className={styles.promptTextarea}
              rows={4}
              style={{ minHeight: "120px", maxHeight: "320px" }}
            />

            <div className={styles.generateButtonWrapper}>
              {isLoading ? (
                <Spinner size="3" />
              ) : (
                <Button
                  onClick={handleGenerate}
                  size="3"
                  color="gray"
                  highContrast
                  disabled={!prompt.trim() || isLoading}
                >
                  Generate
                </Button>
              )}
            </div>

            {apiResponse?.error && (
              <Text size="2" color="red" align="center">
                {apiResponse.error}
              </Text>
            )}
          </div>
        )}

        {step === "review" && (
          <EnrichmentReviewForm
            name={editedName}
            onNameChange={handleNameChange}
            task={editedTask}
            onTaskChange={handleTaskChange}
            enrichmentType={enrichmentType}
            options={editedOptions}
            onOptionsChange={setEditedOptions}

            trigger={editedTrigger}
            onTriggerChange={setEditedTrigger}
            onSubmit={handleSubmit}
            loading={loading}
            submitLabel="Create"
          />
        )}
      </div>
    </div>
  );
}

export default EnrichmentCreationForm;
